import { Auth } from '@angular/fire/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '@core/utils/firebase/firebase';
import type { AlaCarteContent, AlaCarteMenu } from '@core/interfaces/api';
import { createAlaCarteApi } from '../api/alacarte.api';

const alaCarteApi = createAlaCarteApi(auth as Auth);

export function useAlaCarteMenusQuery({
  siteId,
  enabled = true,
}: {
  siteId?: number | null;
  enabled?: boolean;
}) {
  return useQuery<AlaCarteMenu[]>({
    queryKey: ['alacarte-menus', siteId],
    enabled: enabled && !!siteId,
    queryFn: () => alaCarteApi.listForSite(siteId!),
  });
}

export function useCreateAlaCarteMenuMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { siteId: number; title: string; content: AlaCarteContent }) =>
      alaCarteApi.create(data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alacarte-menus', variables.siteId] });
    },
  });
}

export function useUpdateAlaCarteMenuMutation(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ title: string; content: AlaCarteContent }> }) =>
      alaCarteApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alacarte-menus', siteId] });
    },
  });
}

export function useDeleteAlaCarteMenuMutation(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => alaCarteApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alacarte-menus', siteId] });
    },
  });
}
